// Central design tokens for Crescent EdTech — keep colors consistent everywhere.
// Design language: "Vibrant & Credible" — the logo's bright BLUE + PINK act as
// co-primary brand colours, with NAVY as the steady text / dark-surface base.
// Pink is a full working accent (gradients, CTAs, highlights), not a rare tail.
// Tokens are WCAG-checked for text-on-background contrast — note pink/blue are
// used for FILLS, ICONS and BARS on light surfaces, never as small body text
// (see blueInk for the one accessible blue TEXT token).
export const colors = {
  // Navy family — primary brand + dark surfaces
  navy: "#12294a",
  navyDeep: "#0b1c38",
  navySoft: "#1b3a63",

  // Blue family — the working accent
  blue: "#38b6ff", // bright: fills, icons and accents on dark surfaces
  blueDark: "#0d8fe0", // deeper blue: gradients, fills
  blueInk: "#0a5c93", // accessible blue TEXT on light backgrounds (7:1 on white)
  blueTint: "rgba(56,182,255,0.12)", // soft tile / highlight wash

  // Pink — co-primary accent (fills, icons, gradients, CTAs — not body text)
  pink: "#ff66c4",
  pinkDark: "#e14bab",
  pinkDeep: "#c1338c", // deeper pink for accessible-ish icon/border contrast on light
  pinkTint: "rgba(255,102,196,0.12)", // soft pink tile / highlight wash

  // Surfaces & lines
  white: "#ffffff",
  bgLight: "#f5f7fa",
  surfaceAlt: "#f5f7fa",
  border: "#e6edf7",
  borderStrong: "#d7e2f0",

  // Gradients
  bgGradient: "linear-gradient(135deg, #f5f7fa 0%, #e9f0f9 100%)",
  // Professional blue gradient — icon tiles, badges, pills, stat bands.
  brandGradient: "linear-gradient(135deg, #2aa3ea 0%, #0a5f97 100%)",
  // Signature logo gradient — bright blue → pink. Reserved for hero brand
  // moments: primary CTAs, section accent bars, key highlights.
  duoGradient: "linear-gradient(120deg, #38b6ff 0%, #0d8fe0 46%, #ff66c4 100%)",
  // Pink gradient — for pink-led fills (tags, signature chips).
  pinkGradient: "linear-gradient(135deg, #ff7ccd 0%, #e14bab 100%)",
  // Deep navy hero backdrop
  heroGradient: "linear-gradient(140deg, #0b1c38 0%, #12294a 52%, #1b3a63 100%)",

  // Text
  textBody: "#334155",
  textMuted: "#64748b",
};

export const fonts = {
  heading: '"Poppins", "Inter", "Segoe UI", sans-serif',
  body: '"Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
};

// Reusable style fragments so every section shares the same polish.
export const ui = {
  // Soft, premium icon tile — replaces saturated gradient chips on light cards.
  iconTile: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 2,
    bgcolor: colors.blueTint,
    color: colors.blueDark,
    border: `1px solid rgba(56,182,255,0.22)`,
  },
  // Consistent card surface + hover lift with a blue→pink accent glow.
  card: {
    position: "relative",
    bgcolor: colors.white,
    borderRadius: 3,
    border: `1px solid ${colors.border}`,
    boxShadow: "0 1px 2px rgba(18,41,74,0.05)",
    transition:
      "transform .25s cubic-bezier(.2,.7,.3,1), box-shadow .25s ease, border-color .25s ease",
    "&:hover": {
      transform: "translateY(-6px)",
      boxShadow:
        "0 20px 44px rgba(18,41,74,0.12), 0 4px 14px rgba(255,102,196,0.10)",
      borderColor: "rgba(56,182,255,0.55)",
    },
  },
  // Hover-only fragment for LIGHT-surface cards — lift + blue/pink glow.
  // Spread into a card's sx; it doesn't touch the card's own border/bg.
  cardHover: {
    transition:
      "transform .25s cubic-bezier(.2,.7,.3,1), box-shadow .25s ease, border-color .25s ease",
    "&:hover": {
      transform: "translateY(-6px)",
      boxShadow:
        "0 20px 44px rgba(18,41,74,0.12), 0 4px 14px rgba(255,102,196,0.12)",
      borderColor: "rgba(56,182,255,0.55)",
    },
  },
  // Hover-only fragment for DARK / navy-surface cards.
  cardHoverDark: {
    transition:
      "transform .25s cubic-bezier(.2,.7,.3,1), box-shadow .25s ease, border-color .25s ease, background-color .25s ease",
    "&:hover": {
      transform: "translateY(-6px)",
      borderColor: "rgba(56,182,255,0.6)",
      backgroundColor: "rgba(255,255,255,0.08)",
      boxShadow: "0 18px 40px rgba(0,0,0,0.30)",
    },
  },
  // Signature primary CTA — blue→pink gradient with a subtle press/hover motion.
  ctaButton: {
    background: colors.duoGradient,
    backgroundSize: "180% 100%",
    backgroundPosition: "0% 0%",
    color: colors.white,
    borderRadius: "999px",
    fontFamily: fonts.body,
    fontWeight: 700,
    boxShadow: "0 6px 18px rgba(13,143,224,0.28)",
    transition:
      "background-position .5s ease, transform .2s ease, box-shadow .25s ease",
    "&:hover": {
      background: colors.duoGradient,
      backgroundSize: "180% 100%",
      backgroundPosition: "100% 0%",
      transform: "translateY(-2px)",
      boxShadow: "0 10px 26px rgba(225,75,171,0.32)",
    },
  },
};
